'use strict';

/**
 * ATM / Sample Plan Checklist dashboard — configuration catalog.
 *
 * Data-driven description of every value the bespoke #/ATMDashboardConfig screen edits, split across the two
 * surfaces the dashboard actually reads (see documents spec "ATM + Sample Plan Checklist — Configuration Reference"):
 *
 *   1. VALIDATION — the audit logic, Configuration namespace `MaterialsAudit`. The API endpoint reads it as
 *      `Object.assign(defaults, Configuration/For/MaterialsAudit/In/Project/<id>)`, so each TOP-LEVEL key is its
 *      own Configuration NamespaceValue (ContractorReportedQuantityValidation, StatusOptions, …).
 *   2. DISPLAY — which SamplePlan columns show, Configuration namespace `AppDashboards`, under
 *      `<bucket>.SamplePlanChecklist.Config.SamplePlan`. The dashboard (sampleplan.* / dashboards.* iframe) reads
 *      the `SamplePlan` bucket; `hidden:true` hides a column.
 */

// ── Validation (MaterialsAudit) ──────────────────────────────────────────────

// Simple on/off checks — one NamespaceValue each, config shape { Enabled }.
const VALIDATION_CHECKS =
[
	{ Key: 'ContractorReportedQuantityValidation', Label: 'Contractor-reported quantity check', Default: true,
		Description: 'Flag a line item when the installed quantity is short of what the contractor reported (Installed Qty < Item Slip Total).' },
	{ Key: 'PaidQuantityValidation', Label: 'Paid (expected) quantity check', Default: false,
		Description: 'Flag a line item when the installed quantity is short of the expected/paid quantity (Installed Qty < Expected Qty).' },
	{ Key: 'RequiredAcceptedValidation', Label: 'Required samples-accepted check', Default: false,
		Description: 'Flag a line item when fewer samples were accepted than the sample plan requires (a count check).' },
];

// AcceptedQuantityValidation is special — { Enabled, ValidationField }.
const ACCEPTED_QTY_KEY = 'AcceptedQuantityValidation';
const ACCEPTED_QTY_DEFAULT = { Enabled: true, ValidationField: 'ContractorReportedQuantity' };
const ACCEPTED_QTY_FIELDS =
[
	{ Value: 'ContractorReportedQuantity', Label: 'Item Slip Total (contractor reported)' },
	{ Value: 'InstalledQty', Label: 'Installed quantity' },
	{ Value: 'ExpectedQty', Label: 'Expected (paid) quantity' },
	{ Value: 'BidQty', Label: 'Bid quantity' },
];

// StatusOptions — the pass/fail vocabulary. NamespaceValue `StatusOptions`, shape { Passing:[], Failing:[] }.
const STATUS_OPTIONS_KEY = 'StatusOptions';
const STATUS_OPTIONS_DEFAULT = { Passing: [ 'PASS' ], Failing: [ 'FAIL' ] };

// CertUnitTypes — PLIMT unit strings that mean "certification-style" testing. NamespaceValue `CertUnitTypes`
// (its config IS the array).
const CERT_UNIT_TYPES_KEY = 'CertUnitTypes';
const CERT_UNIT_TYPES_DEFAULT =
[
	'per lot per plant per month',
	'per lot per plant per mix design',
	'per source per lot of mineral admixture',
	'project',
];

const MATERIALS_AUDIT_NAMESPACE = 'MaterialsAudit';

// ── Display (AppDashboards → SamplePlan) ─────────────────────────────────────

const APPDASHBOARDS_NAMESPACE = 'AppDashboards';
// The Sample Plan Checklist dashboard reads this bucket (sampleplan.* and the dashboards.* iframe host).
const DISPLAY_BUCKET = 'SamplePlan';

// Canonical SamplePlan-bucket shape (spec §6.3), used to SEED the bucket when a customer/project has no
// AppDashboards config yet — otherwise there is nothing to preserve DashboardHash/EntryPoints against and columns
// can never be configured. Saving with this seed registers the Sample Plan Checklist dashboard for that scope.
const DISPLAY_BUCKET_TEMPLATE =
{
	DashboardHash: 'sampleplan_checklist_new',
	DisplayName: 'Sample Plan Checklist',
	Description: 'Sample plan checklist dashboard.',
	EntryPoints: { css: [ 'index.css' ], js: [ 'index.js' ] },
	Config: { Parent: 'Project', SamplePlan: {} },
};

// Section-level options (live directly under SamplePlan).
const DISPLAY_SECTION_OPTIONS =
[
	{ Key: 'Title', Type: 'text', Label: 'Section title', Default: 'Original Sampling Plan' },
	{ Key: 'Subtitle', Type: 'text', Label: 'Sub-heading (optional)', Default: '' },
	{ Key: 'EmptyMessage', Type: 'text', Label: 'Empty-state message', Default: 'No sample plan found for this Project!' },
	{ Key: 'NonConformingValidation', Type: 'bool', Label: 'Show the green/red conformance indicator column', Default: true },
];

// Column groups. Each column: { Key, Label (default header), Hidden (default) }. The editor shows a "Show" checkbox
// (= !hidden) and an editable label. Labels may contain HTML (the defaults use <br/> to wrap) — shown as raw text
// in the input and written verbatim.
const DISPLAY_COLUMN_GROUPS =
[
	{
		Group: 'Line-item header row',
		Columns:
		[
			{ Key: 'LineItemNumber', Label: 'Line Item', Hidden: false },
			{ Key: 'LineItemCode', Label: 'Item Code', Hidden: false },
			{ Key: 'LineItemDescription', Label: 'Description', Hidden: false },
			{ Key: 'LineItemUnits', Label: 'Units', Hidden: false },
			{ Key: 'ContractQuantity', Label: 'Contract Qty.', Hidden: false },
			{ Key: 'ExpectedQuantity', Label: 'Expected Qty.', Hidden: false },
			{ Key: 'InstalledQuantity', Label: 'Installed Qty.', Hidden: false },
			{ Key: 'ContractorReportedQuantity', Label: 'Item Slip Total', Hidden: true },
			{ Key: 'SmallQtyTotal', Label: 'Small Quantity Total', Hidden: false },
		],
	},
	{
		Group: 'Material / sample-type rows',
		Columns:
		[
			{ Key: 'MaterialCategory', Label: 'Material Category', Hidden: false },
			{ Key: 'SampleUnit', Label: 'Sample <br/>Unit', Hidden: false },
			{ Key: 'SampleType', Label: 'Sample <br/>Type', Hidden: false },
			{ Key: 'SampleRate', Label: 'Rate', Hidden: false },
			{ Key: 'SampleFrequency', Label: 'Frequency', Hidden: false },
			{ Key: 'ConversionFactor', Label: 'Conversion <br/>Factor', Hidden: false },
			{ Key: 'SamplesPlanned', Label: 'Samples <br/>Planned', Hidden: false },
			{ Key: 'SamplesRequired', Label: 'Samples <br/>Required', Hidden: false },
			{ Key: 'SamplesTaken', Label: 'Samples <br/>Taken', Hidden: false },
			{ Key: 'SamplesPassing', Label: 'Samples <br/>Passing', Hidden: false },
			{ Key: 'PartialExceptionTotal', Label: 'Partial <br/>Exception <br/>Quantity', Hidden: false },
			{ Key: 'SamplesAccepted', Label: 'Samples <br/>Accepted', Hidden: true },
			{ Key: 'AcceptedQuantity', Label: 'Accepted Qty.', Hidden: true },
		],
	},
	{
		Group: 'Detail rows (full-width, under each sample type)',
		Columns:
		[
			{ Key: 'SampleDescription', Label: 'Sample Description', Hidden: false },
			{ Key: 'SamplingInstructions', Label: 'Sampling Instructions', Hidden: false },
			{ Key: 'Remarks', Label: 'Remarks', Hidden: false },
			{ Key: 'ErrorOmissionDetail', Label: 'Errors and Omissions', Hidden: false },
		],
	},
];

// The dashboard's built-in fallback profile (spec §4.2) — what the checklist shows when NO SamplePlan config
// exists yet. Supplying a config REPLACES this profile entirely, so when there's no stored config the editor is
// seeded from this (overlaid on the §3.3 column defaults) rather than the bare defaults, and the full state is
// written on save — otherwise saving would silently drop these hidden/relabeled columns. Keys not listed here fall
// back to their §3.3 defaults. (`ErrorsAndOmissionsSummary` is a cover-page/form thing — inert in the dashboard.)
const DISPLAY_FALLBACK_PROFILE =
{
	ContractorReportedQuantity: { label: 'Item Slip Total', hidden: false },
	ConversionFactor: { hidden: true },
	SamplesAccepted: { label: 'Samples <br/>Accepted', hidden: false },
	SamplesPassing: { hidden: true },
	AcceptedQuantity: { label: 'Quantity <br />Accepted', hidden: false },
	SamplingInstructions: { hidden: true },
	Remarks: { hidden: true },
};

// Flat column list + default lookups (built once).
const DISPLAY_COLUMNS = DISPLAY_COLUMN_GROUPS.reduce((pAll, pGroup) => pAll.concat(pGroup.Columns), []);
const DISPLAY_COLUMN_DEFAULTS = {};
DISPLAY_COLUMNS.forEach((pCol) => { DISPLAY_COLUMN_DEFAULTS[pCol.Key] = pCol; });

module.exports =
{
	MATERIALS_AUDIT_NAMESPACE,
	VALIDATION_CHECKS,
	ACCEPTED_QTY_KEY, ACCEPTED_QTY_DEFAULT, ACCEPTED_QTY_FIELDS,
	STATUS_OPTIONS_KEY, STATUS_OPTIONS_DEFAULT,
	CERT_UNIT_TYPES_KEY, CERT_UNIT_TYPES_DEFAULT,
	APPDASHBOARDS_NAMESPACE, DISPLAY_BUCKET, DISPLAY_BUCKET_TEMPLATE,
	DISPLAY_SECTION_OPTIONS, DISPLAY_COLUMN_GROUPS, DISPLAY_COLUMNS, DISPLAY_COLUMN_DEFAULTS, DISPLAY_FALLBACK_PROFILE,
};
